export interface AgreementClause {
  label: string | null;
  text: string;
}

export interface AgreementSection {
  heading: string;
  clauses: AgreementClause[];
}

export const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    heading: '1. Definitions',
    clauses: [
      {
        label: null,
        text: '“Services,” “Platform,” “Order Form,” “Plan,” “Add-On,” “Subscription Term,” “Go-Live Date,” “Restaurant Data,” “Menu Content,” “Diner,” “Diner Data,” “Connected POS,” “POS Provider,” “Devices,” “Documentation,” “Authorized Users,” “Fees” and “Aggregated Data” have the meanings given in this Agreement or the Order Form.',
      },
      {
        label: 'Go-Live Date.',
        text: "The date your Restaurant's listing becomes visible to Diners and ordering is enabled, as confirmed by ForknFind in writing (including by email) or shown in your ForknFind dashboard. Fees begin to accrue under §4 from the Go-Live Date, or from the date specified in your Order Form if earlier.",
      },
    ],
  },
  {
    heading: '2. The Services',
    clauses: [
      {
        label: '2.1 Provision.',
        text: 'We will make the Services identified on your Order Form available to you during the Subscription Term, in accordance with this Agreement and the Documentation.',
      },
      {
        label: '2.2 Modules.',
        text: 'Depending on what you select on the Order Form, the Services may include: marketplace listing in the Fynkle consumer app and on our website; QR-code ordering; Connected POS integration; the ForknFind POS; waiter tablets; a Kitchen Display System (KDS); order management for dine-in, pickup and to-go; loyalty and promotions; reporting and analytics; notifications; and digital marketing.',
      },
      {
        label: '2.3 Not included.',
        text: 'Delivery ordering, payment processing, payroll, inventory management and reservations are not part of the Services unless separately agreed in writing.',
      },
      {
        label: '2.4 Changes to the Services.',
        text: "We may modify, add or discontinue features from time to time. If we discontinue a feature you are paying for as an Add-On, we will give you at least 30 days' notice and, for the remainder of your then-current billing period, a pro-rata credit or the right to terminate that Add-On without penalty.",
      },
      {
        label: '2.5 Beta features.',
        text: 'Any feature marked “beta” or “preview” is provided as-is, without warranty, and may be changed or withdrawn at any time.',
      },
    ],
  },
  {
    heading: '3. Onboarding, Accounts and Authorized Users',
    clauses: [
      {
        label: '3.1 Onboarding information.',
        text: 'You will provide accurate business, contact, location and menu information during onboarding and keep it current.',
      },
      {
        label: '3.2 Existing listings.',
        text: 'You acknowledge that a listing for your Restaurant may already exist on the Platform, compiled from publicly available sources, prior to the Effective Date. As of the Effective Date, you confirm, adopt and/or correct that listing, and you grant the licences described in §8 with respect to it.',
      },
      {
        label: '3.3 Authorized Users.',
        text: 'You are responsible for your Authorized Users, for safeguarding their credentials, and for all activity under your account. You will notify us promptly of any unauthorized access.',
      },
      {
        label: '3.4 Staff roles.',
        text: 'Staff roles and permissions within the Services are yours to configure and audit.',
      },
    ],
  },
  {
    heading: '4. Fees, Billing and the Free Period',
    clauses: [
      {
        label: '4.1 Fees.',
        text: 'You will pay the Fees for the Plan and Add-Ons you select in your ForknFind admin portal (or, where applicable, as set out on your Order Form). Base Plan fees are billed monthly in advance. Current list pricing is published in your admin portal at the time you select or change your Plan. There is no long-term contract — Plans bill month-to-month and you may cancel under §12.',
      },
      {
        label: '4.2 Add-Ons.',
        text: 'Add-Ons (for example Digital Marketing, KDS, Waiter Tablet or Food Runner) are billed in addition to your Plan, either at a flat monthly rate or per device/seat activated, as shown on your Order Form. An Add-On activated mid-period is billed starting with your next billing cycle, unless your Order Form says otherwise.',
      },
      {
        label: '4.3 Early-adopter free period.',
        text: 'If your Order Form grants a free period, your base Plan fee is $0 for that number of months; Add-Ons bill at full price during the free period. We will email you at least 14 days before the free period ends, stating the standard Plan Fee that will apply. At the end of the free period, your subscription automatically continues and you will be charged the standard Plan Fee unless you cancel under §12 before the free period ends.',
      },
      {
        label: '4.4 Payment method.',
        text: 'You authorize us and our payment processor to charge your designated payment method for all Fees when due. You will keep your payment details current.',
      },
      {
        label: '4.5 Taxes.',
        text: 'Fees are exclusive of taxes. You are responsible for any applicable sales, use or similar taxes on the Fees, other than taxes on our income.',
      },
      {
        label: '4.6 Late payment and suspension.',
        text: "Amounts unpaid 10 days after the due date may accrue interest at the lesser of 1.5% per month or the maximum rate permitted by law. If Fees remain unpaid 15 days after the due date, we may suspend the Services, including your marketplace listing and QR ordering, on 5 days' written notice.",
      },
      {
        label: '4.7 Price changes.',
        text: "We may change our list prices at any time; any change to your Fees takes effect at the start of your next renewal period, on at least 30 days' notice. Existing subscriptions keep the price they were created under until you change Plans.",
      },
      {
        label: '4.8 No refunds.',
        text: 'Fees are non-refundable except as required by law or expressly stated in this Agreement.',
      },
    ],
  },
  {
    heading: '5. Connected POS Authorization',
    clauses: [
      {
        label: '5.1 Your authorization.',
        text: "You authorize us to connect to your point-of-sale account with your POS Provider (currently Square or Clover) through that provider's authorization flow, and to store and use the resulting access credentials for as long as the integration is active.",
      },
      {
        label: '5.2 Scope.',
        text: 'You authorize us, acting solely on your instruction, to: read your catalog, categories, modifiers, locations and merchant information; create, read, update and search orders; initiate payments and terminal checkouts through your POS account for orders placed via the Services; trigger kitchen tickets; and receive webhook notifications. We act only within this authorization — we are not a collection agent for you and we do not take custody of your funds.',
      },
      {
        label: '5.3 Your POS relationship is yours.',
        text: 'Your agreement with your POS Provider governs your POS account. You are responsible for keeping it in good standing, for its fees, and for complying with its terms. We are not a party to that agreement.',
      },
      {
        label: '5.4 No responsibility for the POS Provider.',
        text: 'We are not responsible for POS Provider outages, API changes, deprecations, rate limits, data errors, pricing changes, or termination of your POS account, or for any resulting interruption of the Services.',
      },
      {
        label: '5.5 Disconnection.',
        text: 'You may disconnect the integration at any time. Doing so may disable ordering, menu sync and payment features through the Services and does not reduce the Fees owed.',
      },
    ],
  },
  {
    heading: '6. Payments for Diner Orders — No Funds Custody',
    clauses: [
      {
        label: '6.1 How payment works.',
        text: "Payments for orders placed through the Services are processed through your own POS or payment provider account, using that provider's payment stack. Funds settle directly to you.",
      },
      {
        label: '6.2 We are not the merchant of record.',
        text: 'ForknFind does not process card payments, is not the merchant of record for Diner orders, and at no time takes custody or control of Diner funds. We do not act as a payment intermediary, money transmitter or collection agent for Diner orders.',
      },
      {
        label: '6.3 Your responsibility.',
        text: 'You are solely responsible for refunds, chargebacks, disputes, reversals, card-network and processing fees, payout timing, PCI DSS obligations attaching to your POS account, and the tax treatment of your sales.',
      },
      {
        label: '6.4 Price and fee display.',
        text: 'You are responsible for the accuracy of the prices, taxes, service fees, gratuity options and any other charges configured for your menu and checkout, and for their compliance with applicable law, including California price-transparency requirements. We build the checkout interface; you configure the numbers displayed in it.',
      },
      {
        label: '6.5 No per-order fee today.',
        text: 'We do not currently charge a per-order commission or transaction fee on Diner orders. If we introduce a processing service or per-order fee in the future, we will give you advance notice and an updated Order Form or amendment before it applies to you.',
      },
    ],
  },
  {
    heading: '7. Your Obligations',
    clauses: [
      {
        label: '7.1 Licences and compliance.',
        text: 'You will hold and maintain all licences, permits and registrations required to operate your business, including health permits and, where applicable, alcoholic beverage licences, and will comply with all applicable food safety, employment, consumer protection, advertising and tax laws.',
      },
      {
        label: '7.2 Menu accuracy.',
        text: 'You are solely responsible for the accuracy and completeness of your Menu Content, including prices, availability, ingredients, and allergen and nutrition information, and for keeping it current in your POS or our dashboard.',
      },
      {
        label: '7.3 Order fulfilment.',
        text: 'You will accept and fulfil orders placed through the Services on the same basis as orders placed directly, will keep your hours and availability accurate, and will not discriminate against Diners who order through the Platform.',
      },
      {
        label: '7.4 Age-restricted items.',
        text: 'You will not offer alcohol or other age-restricted items through QR-code ordering unless (a) you hold a valid alcoholic beverage licence authorizing such sales, and (b) you require in-person age verification by your staff before the order is fulfilled or handed off. The Services do not currently perform automated age verification.',
      },
      {
        label: '7.5 Devices and premises.',
        text: 'You will provide network connectivity, power and a suitable environment for any Devices, and will use them in accordance with the Documentation.',
      },
      {
        label: '7.6 Staff.',
        text: 'You will train your staff on use of the Services and are responsible for their use of it.',
      },
      {
        label: '7.7 Acceptable use.',
        text: 'You will comply with our Acceptable Use Policy, including not reverse engineering, scraping, or reselling access to the Services outside your Authorized Users, and not circumventing technical limits.',
      },
      {
        label: '7.8 Insurance.',
        text: 'You will maintain commercial general liability insurance, and product liability insurance if you sell food or beverage products, with limits of at least $1,000,000 per occurrence, and will provide a certificate of insurance naming ForknFind as an additional insured within 10 days of request.',
      },
    ],
  },
  {
    heading: '8. Restaurant Data, Menu Content and Brand Licence',
    clauses: [
      {
        label: '8.1 Ownership.',
        text: 'You retain all rights in Restaurant Data and Menu Content.',
      },
      {
        label: '8.2 Licence to us.',
        text: 'You grant us a worldwide, non-exclusive, royalty-free licence during the Subscription Term to host, store, reproduce, adapt, translate, format, publicly display and distribute Restaurant Data and Menu Content, and to use your name, logo and trademarks, in each case to: operate and provide the Services; list and promote your business in the Fynkle consumer app, on our websites, and in app-store listings; and produce marketing and promotional materials for the Platform generally.',
      },
      {
        label: '8.3 AI and machine learning.',
        text: 'You further grant us the right to process Restaurant Data and Menu Content using automated and machine-learning systems, including third-party AI sub-processors, to generate search indexes and embeddings, recommendations, descriptive text and menu imagery, and to improve the Services.',
      },
      {
        label: '8.4 AI-generated content.',
        text: 'Imagery or text we generate for your listing is owned by ForknFind and licensed to you for use in connection with the Services. You may request its removal or correction at any time.',
      },
      {
        label: '8.5 Aggregated Data.',
        text: 'We may create and use aggregated and de-identified data derived from your use of the Services for analytics, benchmarking, research and product development, provided it does not identify you or any Diner.',
      },
      {
        label: '8.6 Your warranties.',
        text: 'You warrant that you own or have all rights necessary to grant the licences in this §8, including for all menu photographs, third-party marks and any content synced from your POS, and that our permitted use of it will not infringe any third party’s rights.',
      },
      {
        label: '8.7 Reviews and ratings.',
        text: 'Diner reviews, ratings and other Diner-generated content are governed by our consumer terms. You do not have a right to require removal of a review, other than through our published content-moderation process.',
      },
      {
        label: '8.8 Survival.',
        text: 'On termination, we will remove your listing within 15 days; the licence in §8.2 survives only to the extent necessary for archival copies, Aggregated Data, and materials already distributed before termination.',
      },
    ],
  },
  {
    heading: '9. Diner Data and Privacy',
    clauses: [
      {
        label: '9.1 Direct relationship.',
        text: 'Diners have a direct relationship with ForknFind and Fynkle, and their personal information is handled under our consumer privacy policy at fynkle.com/privacy-policy.',
      },
      {
        label: '9.2 Roles.',
        text: 'For personal information we collect in operating the Fynkle consumer app and our direct relationship with Diners, ForknFind acts as a business in its own right under the CCPA/CPRA. Where we process Restaurant Data or Diner order information solely to provide the Services on your instruction, we act as a service provider to you.',
      },
      {
        label: '9.3 What you receive.',
        text: 'We will provide you with order-level information necessary to fulfil orders and operate loyalty, and reporting as described in your Order Form. Except as set out in your Order Form, you will not receive Diner contact details and may not use any Diner information for your own marketing, or disclose it to any third party.',
      },
      {
        label: '9.4 Your compliance.',
        text: 'To the extent you do receive any Diner personal information, you will process it only to fulfil orders and provide customer service, will not sell or share it, and will comply with applicable privacy law.',
      },
      {
        label: '9.5 Consumer requests.',
        text: 'We will handle Diner access, deletion and opt-out requests directly. If you receive such a request, you will refer it to us within 5 business days and cooperate as reasonably needed.',
      },
      {
        label: '9.6 Security incidents.',
        text: 'Each party will maintain reasonable technical and organizational security measures. We will notify you without undue delay, and in any event within 72 hours, of confirming a security incident affecting your Restaurant Data.',
      },
      {
        label: '9.7 Sub-processors.',
        text: "Our current sub-processors for the Services are listed in our Data Processing Terms. We may update this list on 30 days' notice.",
      },
      {
        label: '9.8 Retention and deletion.',
        text: 'We will use commercially reasonable efforts to delete or de-identify Restaurant Data and associated order data within 12 months of collection, except where we need to retain it to comply with law, resolve disputes, enforce our agreements, or as Aggregated Data.',
      },
      {
        label: '9.9 Data Processing Terms.',
        text: 'Our Data Processing Terms are incorporated by reference and control in the event of a conflict on data protection matters.',
      },
    ],
  },
  {
    heading: '10. Loyalty, Promotions and Rewards',
    clauses: [
      {
        label: '10.1 Programme.',
        text: 'We operate a Platform-wide loyalty and rewards programme for Diners, including points, third-party gift-card rewards (for example, Amazon or eBay gift cards), and referrals.',
      },
      {
        label: '10.2 Funding and settlement.',
        text: 'Loyalty points earned through the Services convert to third-party gift cards that ForknFind sources and provides directly to Diners. This reward is independent of your payment flow: it does not reduce the amount you receive for any order, and you have no funding, reimbursement, or redemption obligation with respect to it.',
      },
      {
        label: '10.3 Honouring rewards.',
        text: 'You will honour valid rewards, discounts and promotions presented through the Services in accordance with their terms.',
      },
      {
        label: '10.4 Your own promotions.',
        text: 'Promotions you configure yourself are your responsibility, including their terms and legal compliance.',
      },
      {
        label: '10.5 On termination.',
        text: 'Diner loyalty points attributable to purchases at your Restaurant are a Platform-wide liability and are not extinguished by your termination; ForknFind remains responsible for honouring them elsewhere on the Platform.',
      },
    ],
  },
  {
    heading: '11. Devices and Hardware',
    clauses: [
      {
        label: '11.1 Loan of Devices.',
        text: 'Devices listed on your Order Form (for example waiter tablets, KDS screens, printers or POS terminals) are loaned to you for use during the Subscription Term. Title remains with ForknFind at all times; this is a bailment, not a sale or rental.',
      },
      {
        label: '11.2 Remote management.',
        text: 'You acknowledge and consent that Devices are enrolled in our mobile device management system, that we may remotely configure, update, restrict, lock or wipe them, and that they are provisioned for use with the Services and are not general-purpose computing devices.',
      },
      {
        label: '11.3 Care and return.',
        text: 'You will not modify, unenroll, relocate off premises, or transfer Devices. On termination, you will return all Devices in good working order within 15 days, or pay their then-current replacement value.',
      },
      {
        label: '11.4 Warranty and support.',
        text: 'We will pass through any manufacturer warranty available to us. Subject to that warranty, we will repair or replace a defective Device at our cost; you are responsible for shipping a Device to us for service unless the defect is confirmed to be a manufacturing defect.',
      },
    ],
  },
  {
    heading: '12. Term, Cancellation and Termination',
    clauses: [
      {
        label: '12.1 Term.',
        text: 'This Agreement begins on the Effective Date and continues until terminated. Each subscription renews monthly unless cancelled.',
      },
      {
        label: '12.2 Cancellation by you.',
        text: 'You may cancel your subscription at any time, effective at the end of your then-current monthly billing period, by emailing us at the support address on your Order Form at least 5 days before your renewal date. No refund or credit is owed for the remainder of a period already paid for.',
      },
      {
        label: '12.3 Termination for cause.',
        text: "Either party may terminate on 30 days' written notice of a material breach that remains uncured. We may also suspend or terminate immediately for non-payment beyond §4.6, for a violation of §7, or where continued provision of the Services would expose us or Diners to a legal or safety risk.",
      },
      {
        label: '12.4 Termination for convenience by us.',
        text: "We may terminate this Agreement for convenience on 30 days' written notice.",
      },
      {
        label: '12.5 Effect of termination.',
        text: 'Your access ends on the termination date; your listing is removed under §8.8. Orders already placed before the termination date will be completed where reasonably possible; no new orders will be accepted after that date. Accrued Fees become immediately due. Devices are returned under §11.3. Loyalty points are treated under §10.5.',
      },
      {
        label: '12.6 Data export.',
        text: 'For 30 days after termination, we will make your Restaurant Data available for export in CSV or JSON format through your dashboard on request. After that period, we will delete or de-identify it in accordance with §9.8.',
      },
      {
        label: '12.7 Survival.',
        text: 'Sections 1, 4 (accrued amounts), 6.3, 7.1, 8.5–8.6, 8.8, 9, and 13–17 survive termination.',
      },
    ],
  },
  {
    heading: '13. Confidentiality',
    clauses: [
      {
        label: null,
        text: "Each party may receive confidential information of the other, including this Agreement's pricing and terms, and (for our Confidential Information) our product roadmap and non-public technical information. The receiving party will use the other's Confidential Information only to perform this Agreement, and will protect it with the same care it uses for its own similarly sensitive information (and no less than reasonable care). This obligation does not apply to information that is public, independently known, independently developed, or rightfully received from a third party without restriction, and does not prevent disclosure compelled by law. These obligations continue for 3 years after disclosure, except that trade secrets remain protected for as long as they qualify as trade secrets under applicable law.",
      },
    ],
  },
  {
    heading: '14. Intellectual Property',
    clauses: [
      {
        label: '14.1 Our IP.',
        text: 'We and our licensors own the Platform, the Services, the ForknFind and Fynkle marks, and all related software, models, indexes and derived materials, excluding Restaurant Data and Menu Content.',
      },
      {
        label: '14.2 Limited licence to you.',
        text: 'No rights are granted to you except the limited right to use the Services during the Subscription Term in accordance with this Agreement.',
      },
      {
        label: '14.3 Feedback.',
        text: 'You grant us a perpetual, irrevocable, royalty-free licence to use suggestions and feedback you give us about the Services, without obligation to you.',
      },
      {
        label: '14.4 Publicity.',
        text: 'We may display and reference your business name and logo as part of your Platform listing and in materials depicting the Platform generally. Any other use — such as a case study, press release, or use of your name outside the context of your Platform listing — requires your prior written consent.',
      },
    ],
  },
  {
    heading: '15. Warranties and Disclaimer',
    clauses: [
      {
        label: '15.1 Mutual authority.',
        text: 'Each party warrants it has the authority to enter into this Agreement.',
      },
      {
        label: '15.2 Our commitment.',
        text: 'We warrant that we will provide the Services with reasonable skill and care. We do not currently commit to a specific uptime service level; we will use commercially reasonable efforts to keep the Services available and will notify you of planned maintenance where practical.',
      },
      {
        label: '15.3 Disclaimer.',
        text: 'Except as expressly stated in this Agreement, the Services are provided “as is,” and we disclaim all implied warranties, including merchantability, fitness for a particular purpose, accuracy and non-infringement. We do not warrant uninterrupted or error-free operation, and we specifically disclaim responsibility for third-party services, including your POS Provider, payment providers, app stores, networks and AI providers.',
      },
    ],
  },
  {
    heading: '16. Indemnities and Limitation of Liability',
    clauses: [
      {
        label: '16.1 Your indemnity.',
        text: 'You will defend and indemnify us against third-party claims arising from: your food, beverages or services, including illness, injury, allergen and food-safety claims; the accuracy of your Menu Content and pricing; your Restaurant Data, Menu Content and marks, including any infringement claim relating to them; your breach of §7; and your handling of any Diner personal information you receive.',
      },
      {
        label: '16.2 Our indemnity.',
        text: 'We will defend and indemnify you against third-party claims that the Services, as provided by us and used as permitted, infringe a U.S. intellectual property right, excluding claims arising from Restaurant Data, Menu Content, your modifications, or combination with third-party services.',
      },
      {
        label: '16.3 Cap.',
        text: "Except for the carve-outs in §16.4, each party's aggregate liability arising out of this Agreement is limited to the Fees paid or payable by you in the 12 months preceding the claim.",
      },
      {
        label: '16.4 Carve-outs from the cap.',
        text: 'The cap in §16.3 does not apply to: your indemnity obligations under §16.1; your unpaid Fees; either party’s breach of §13 (Confidentiality); or either party’s fraud, wilful misconduct or gross negligence.',
      },
      {
        label: '16.5 Excluded damages.',
        text: "Neither party is liable for indirect, incidental, special, consequential or punitive damages, or for lost profits, revenue, goodwill or data, even if advised of the possibility, except to the extent such damages are the direct result of a party's gross negligence or wilful misconduct.",
      },
    ],
  },
  {
    heading: '17. General',
    clauses: [
      {
        label: '17.1 Governing law and venue.',
        text: 'This Agreement is governed by California law, without regard to conflict-of-law rules. Any dispute arising out of this Agreement will be brought exclusively in the state or federal courts located in Santa Clara County, California, and each party consents to personal jurisdiction and venue there.',
      },
      {
        label: '17.2 Changes to this Agreement.',
        text: "We may update this Agreement on 30 days' notice. If a change is materially adverse to you, you may terminate without penalty before it takes effect by notifying us; otherwise, continued use of the Services after the effective date constitutes acceptance.",
      },
      {
        label: '17.3 Order of precedence.',
        text: 'In case of conflict: the Order Form, then our Data Processing Terms, then this Agreement, then the Documentation.',
      },
      {
        label: '17.4 Assignment.',
        text: "Neither party may assign this Agreement without the other's consent, except to a successor in a merger, acquisition, or sale of substantially all assets.",
      },
      {
        label: '17.5 Notices.',
        text: 'Notices must be sent to the addresses on the Order Form. Routine billing and service notices may be given in-product or by email.',
      },
      {
        label: '17.6 Communications consent.',
        text: 'You consent to receive operational, billing and service emails, SMS and push notifications at the contact details you provide, and you represent that you have authority to provide any staff contact details you give us for that purpose.',
      },
      {
        label: '17.7 Force majeure.',
        text: 'Neither party is liable for delay or failure to perform caused by events beyond its reasonable control.',
      },
      {
        label: '17.8 Independent contractors.',
        text: 'The parties are independent contractors. Nothing in this Agreement creates a partnership, franchise, joint venture, or agency relationship, except for the limited authorization in §5.1–5.2.',
      },
      {
        label: '17.9 Entire agreement.',
        text: 'This Agreement, together with the Order Form and Exhibits, is the entire agreement between the parties on its subject matter and supersedes prior discussions. If any provision is unenforceable, the rest remains in effect. Failure to enforce a provision is not a waiver. This Agreement may be signed in counterparts and by electronic signature.',
      },
    ],
  },
];
